import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, RefreshCw, Lock } from "lucide-react";
import { Nav } from "../components/v2/Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Pill = ({ ok, yes, no }) =>
  ok ? (
    <span className="font-mono-brand text-[9px] tracking-[0.2em] uppercase bg-[#FF4D0A] text-[#0B0B0C] px-2 py-1 font-bold whitespace-nowrap">{yes}</span>
  ) : (
    <span className="font-mono-brand text-[9px] tracking-[0.2em] uppercase text-[#6E7178] border border-[#6E7178]/40 px-2 py-1 whitespace-nowrap">{no}</span>
  );

const Stat = ({ label, value, testId }) => (
  <div className="border border-[#F2F0EB]/12 px-6 py-7" data-testid={testId}>
    <div className="h-heavy text-5xl sm:text-6xl text-[#FF4D0A] leading-none">{value}</div>
    <div className="font-mono-brand text-[10px] tracking-[0.25em] uppercase text-[#9DA1A8] mt-3">{label}</div>
  </div>
);

const KeyGate = ({ input, setInput, onSubmit, loading }) => (
  <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center px-5">
    <div className="w-full max-w-md">
      <div className="bg-[#FF4D0A] p-8 sm:p-10">
        <p className="font-mono-brand text-[10px] tracking-[0.3em] uppercase font-bold text-[#0B0B0C]">Private · Sourcecoded</p>
        <h1 className="h-heavy text-6xl text-[#0B0B0C] mt-4 leading-[0.92]" data-testid="claims-gate-headline">
          The<br /><span className="text-[#F2F0EB]">ledger.</span>
        </h1>
      </div>
      <form onSubmit={onSubmit} className="bg-[#F2F0EB] p-8 sm:p-10" data-testid="claims-key-form">
        <label className="font-mono-brand text-[10px] tracking-[0.25em] uppercase text-[#8A8378] font-bold">Access key</label>
        <input
          type="password"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="••••••••••••"
          className="mt-3 w-full border-2 border-[#0B0B0C] bg-transparent text-[#0B0B0C] font-mono-brand text-sm px-4 py-3 focus:outline-none focus:border-[#FF4D0A] transition-colors"
          data-testid="claims-key-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full bg-[#0B0B0C] text-[#FF4D0A] h-heavy text-xl py-4 hover:bg-[#1a1c20] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="claims-key-submit"
        >
          {loading ? "Checking…" : "Unlock"} {!loading && <ArrowRight size={18} />}
        </button>
      </form>
    </div>
  </div>
);

export default function Claims() {
  const [key, setKey] = useState(() => sessionStorage.getItem("sc_admin_key") || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (k) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/claims`, { headers: { "X-Admin-Key": k } });
      setData(res.data);
      sessionStorage.setItem("sc_admin_key", k);
      setKey(k);
    } catch (err) {
      sessionStorage.removeItem("sc_admin_key");
      setKey("");
      setData(null);
      if (err?.response?.status === 401) toast.error("Wrong key.");
      else toast.error("Could not load the ledger. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    sessionStorage.removeItem("sc_admin_key");
    setKey("");
    setData(null);
    setInput("");
  };

  if (!data) {
    if (key && loading) {
      return (
        <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
          <p className="font-mono-brand text-xs tracking-[0.3em] uppercase text-[#9DA1A8]">Opening the ledger…</p>
        </div>
      );
    }
    return (
      <KeyGate
        input={input}
        setInput={setInput}
        loading={loading}
        onSubmit={(e) => { e.preventDefault(); if (input.trim()) load(input.trim()); }}
      />
    );
  }

  const { stats, claims, waitlist } = data;

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-[#F2F0EB] md:pt-16" data-testid="claims-dashboard">
      <Nav />
      <header className="flex items-center justify-end px-5 sm:px-8 py-3 border-b border-[#F2F0EB]/10">
        <div className="flex items-center gap-4">
          <button onClick={() => load(key)} className="text-[#9DA1A8] hover:text-[#FF4D0A] transition-colors" title="Refresh" data-testid="claims-refresh-button">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={logout} className="text-[#9DA1A8] hover:text-[#FF4D0A] transition-colors" title="Lock" data-testid="claims-lock-button">
            <Lock size={15} />
          </button>
        </div>
      </header>

      <div className="bg-[#FF4D0A] text-[#0B0B0C] px-5 sm:px-8 py-12 sm:py-16">
        <div className="mx-auto max-w-6xl">
          <p className="font-mono-brand text-[10px] tracking-[0.3em] uppercase font-bold">Private · Claims dashboard</p>
          <h1 className="h-heavy text-5xl sm:text-7xl leading-[0.92] mt-4" data-testid="claims-headline">
            Every code<br /><span className="text-[#F2F0EB]">claimed.</span>
          </h1>
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-10 sm:py-14">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Waitlist emails" value={stats.waitlist} testId="stat-waitlist" />
          <Stat label="Codes claimed" value={stats.claims} testId="stat-claims" />
          <Stat label="Community invited" value={stats.community_invites} testId="stat-community" />
        </div>

        <section className="mt-14" data-testid="claims-section">
          <div className="flex items-baseline gap-4">
            <h2 className="h-heavy text-3xl sm:text-4xl">Code claims</h2>
            <span className="font-mono-brand text-xs text-[#FF4D0A]">{claims.length}</span>
          </div>
          <div className="mt-5 bg-[#F2F0EB] text-[#0B0B0C]" data-testid="claims-table">
            {claims.length === 0 ? (
              <p className="font-mono-brand text-xs tracking-[0.2em] uppercase text-[#8A8378] px-6 py-10">No codes claimed yet.</p>
            ) : (
              claims.map((c) => (
                <div key={c.email} className="grid grid-cols-1 lg:grid-cols-[1.4fr_1.2fr_1fr_auto] gap-2 lg:gap-6 items-center px-5 sm:px-6 py-5 border-b-2 border-[#0B0B0C]/90 last:border-b-0" data-testid={`claim-row-${c.email}`}>
                  <div>
                    <div className="h-heavy text-xl leading-tight">{c.first_name || "No name"}</div>
                    <div className="font-mono-brand text-[11px] text-[#45403A] mt-1 break-all">{c.email}</div>
                  </div>
                  <div>
                    <div className="font-mono-brand text-[12px] text-[#FF4D0A] font-bold break-all">{c.source_id}</div>
                    <div className="font-mono-brand text-[10px] tracking-[0.15em] uppercase text-[#8A8378] mt-1">
                      {[c.sun, c.moon, c.rising].filter(Boolean).join(" · ") || "No baseline data"}
                    </div>
                  </div>
                  <div className="font-mono-brand text-[10px] tracking-wide uppercase text-[#8A8378]">{fmtDate(c.created_at)}</div>
                  <div className="justify-self-start lg:justify-self-end">
                    <Pill ok={c.community_invited} yes="Invited" no="Not invited" />
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="mt-14" data-testid="waitlist-section">
          <div className="flex items-baseline gap-4">
            <h2 className="h-heavy text-3xl sm:text-4xl">Waitlist</h2>
            <span className="font-mono-brand text-xs text-[#FF4D0A]">{waitlist.length}</span>
          </div>
          <div className="mt-5 border border-[#F2F0EB]/12" data-testid="waitlist-table">
            {waitlist.length === 0 ? (
              <p className="font-mono-brand text-xs tracking-[0.2em] uppercase text-[#6E7178] px-6 py-10">No waitlist emails yet.</p>
            ) : (
              waitlist.map((w) => (
                <div key={w.email} className="grid grid-cols-1 sm:grid-cols-[1.6fr_1fr_auto_auto] gap-2 sm:gap-6 items-center px-5 sm:px-6 py-4 border-b border-[#F2F0EB]/10 last:border-b-0" data-testid={`waitlist-row-${w.email}`}>
                  <div className="font-mono-brand text-[12px] break-all">{w.email}</div>
                  <div className="font-mono-brand text-[10px] tracking-wide uppercase text-[#6E7178]">{fmtDate(w.created_at)}</div>
                  <Pill ok={w.claimed_code} yes="Code claimed" no="No code" />
                  <Pill ok={w.community_invited} yes="Invited" no="Not invited" />
                </div>
              ))
            )}
          </div>
        </section>

        <p className="font-mono-brand text-[10px] tracking-[0.25em] uppercase text-[#6E7178] mt-14">
          Not prediction. Direction.
        </p>
      </main>
    </div>
  );
}
